import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import { Modal } from './Modal';
import Button from './Button';
import { useLimitContext } from '../../contexts/LimitContext';

const LIMIT_TYPE_LABELS: Record<string, { title: string; description: string; icon?: string }> = {
  serviceareas: {
    title: 'Service Area Limit Reached',
    description: 'You have reached the maximum number of service areas allowed on your current plan.',
  },
  leads: {
    title: 'Lead Limit Reached',
    description: 'You have reached the maximum number of leads allowed on your current plan.',
  },
  users: {
    title: 'User Limit Reached',
    description: 'You have reached the maximum number of team members allowed on your current plan.',
  },
  installations: {
    title: 'Installation Limit Reached',
    description: 'You have reached the maximum number of installations allowed on your current plan.',
  },
  addresses: {
    title: 'Address Limit Reached',
    description: 'You have reached the maximum number of addresses allowed on your current plan.',
  },
  default: {
    title: 'Limit Reached',
    description: 'You have reached a limit on your current plan.',
  },
};

export function LimitExceededModal() {
  const { limitError, clearLimitError } = useLimitContext();
  const navigate = useNavigate();

  if (!limitError) return null;

  const limitInfo = LIMIT_TYPE_LABELS[limitError.limitType] || LIMIT_TYPE_LABELS.default;

  const handleUpgrade = () => {
    clearLimitError();
    navigate('/dashboard/settings?tab=subscription');
  };

  return (
    <Modal
      isOpen={!!limitError}
      onClose={clearLimitError}
      size="sm"
      showCloseButton={false}
    >
      <div className="text-center">
        {/* Warning Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-warning" />
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-text mb-2">
          {limitInfo.title}
        </h3>

        {/* Description */}
        <p className="text-text-secondary mb-2">
          {limitInfo.description}
        </p>

        {/* Show the actual error message from backend if different */}
        {limitError.message && !limitError.message.includes('limit reached') && (
          <p className="text-sm text-text-secondary bg-surface-secondary rounded-lg p-3 mb-4">
            {limitError.message}
          </p>
        )}

        {/* Usage info if available */}
        {limitError.currentUsage !== undefined && limitError.limit !== undefined && (
          <div className="bg-surface-secondary rounded-lg p-4 mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-text-secondary">Current Usage</span>
              <span className="font-medium text-text">
                {limitError.currentUsage} / {limitError.limit}
              </span>
            </div>
            <div className="w-full bg-border rounded-full h-2">
              <div
                className="bg-warning h-2 rounded-full"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* CTA */}
        <p className="text-sm text-text-secondary mb-6">
          Upgrade your subscription to increase your limits and unlock more features.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            onClick={handleUpgrade}
            rightIcon={<ArrowRight size={18} />}
            className="w-full"
          >
            View Subscription Options
          </Button>
          <button
            onClick={clearLimitError}
            className="text-sm text-text-secondary hover:text-text transition-colors flex items-center justify-center gap-1"
          >
            <X size={14} />
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
